import { Tooltip } from '@mui/material';
import { FC, ReactElement, useState } from 'react';
import { fetchInvoicePaymentHistory } from '../../store/CashInflowStore/cashInflowThunk';
import { fetchPurchaseInvoicePaymentHistory } from '../../store/CashOutflowStore/cashOutflowThunk';
import { useAppDispatch } from '../../store/hooks';
import { Utils, VocuherCategory } from '../../utils/common-utils';

interface PaymentStatusTooltipProps {
  row: any;
  type: string; // VocuherCategory.SALE | VocuherCategory.PURCHASE
  children: ReactElement;
}

// Same row style as the "Total" column tooltip in InvoiceList
const rowStyle = { display: 'flex', justifyContent: 'space-between', gap: 16 } as const;
// Thin line between payments and above the balance (tooltip background is dark).
const dividerStyle = { borderTop: '1px solid rgba(255, 255, 255, 0.3)', marginTop: 6, paddingTop: 6 } as const;

// Payment modes as stored (uppercase) → display label. Shared with InvoiceList.
export const PAYMENT_MODE_LABELS: Record<string, string> = {
  CASH: 'Cash',
  UPI: 'UPI',
  NETBANKING: 'Net Banking',
  CARD: 'Card',
  CHEQUE: 'Cheque'
};

/**
 * Tooltip on the Paid / Partially Paid / Over Paid status chip.
 * Styled exactly like the "Total" column tooltip: one line per payment
 * (date, receipt no., pay mode, amount), then Balance Amount.
 * Payments are fetched lazily on first hover.
 */
const PaymentStatusTooltip: FC<PaymentStatusTooltipProps> = ({ row, type, children }) => {
  const dispatch = useAppDispatch();
  const [payments, setPayments] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(false);

  const isPurchase = type === VocuherCategory.PURCHASE;

  const loadPayments = async () => {
    if (payments !== null || loading || !row?._id) return;
    setLoading(true);
    try {
      const thunk = isPurchase ? fetchPurchaseInvoicePaymentHistory : fetchInvoicePaymentHistory;
      const res: any = await dispatch(thunk(row._id));
      const list: any[] = res?.payload?.payments || [];
      setPayments([...list].sort((a, b) => new Date(a.appliedDate).getTime() - new Date(b.appliedDate).getTime()));
    } catch (e) {
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip
      arrow
      onOpen={loadPayments}
      title={
        <div style={{ fontSize: '12px', lineHeight: '1.8', padding: '4px 0' }}>
          {loading && <div>Loading...</div>}
          {payments && payments.map((p: any, index: number) => {
            // sale → paymentReceipt, purchase → paymentMade
            const parent = (p.paymentReceipt && typeof p.paymentReceipt === 'object') ? p.paymentReceipt
              : (p.paymentMade && typeof p.paymentMade === 'object') ? p.paymentMade : {};
            const receiptNo = parent.txnId ?? p.txnId;
            const mode = String(parent.paymentMode || '');
            return (
              <div key={p._id} style={index > 0 ? dividerStyle : undefined}>
                <div style={rowStyle}><span>Date</span><span>{Utils.formatDate(parent.paymentDate || p.appliedDate)}</span></div>
                {receiptNo != null && <div style={rowStyle}><span>Receipt No.</span><span>#{receiptNo}</span></div>}
                {mode && <div style={rowStyle}><span>Pay Mode</span><span>{PAYMENT_MODE_LABELS[mode.toUpperCase()] || mode}</span></div>}
                <div style={rowStyle}><span>Paid Amount</span><span>{Utils.formatNumber(parseFloat(p.appliedAmountTotal) || 0, true, true)}</span></div>
              </div>
            );
          })}
          <div style={{ ...rowStyle, ...(payments && payments.length > 0 ? dividerStyle : {}) }}><span>Balance Amount</span><span>{Utils.formatNumber(row.balanceAmount || 0, true, true)}</span></div>
        </div>
      }
    >
      <span style={{ cursor: 'pointer', display: 'inline-flex' }}>{children}</span>
    </Tooltip>
  );
};

export default PaymentStatusTooltip;
